import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import {
    ErrorText,
    FlexContainer,
    LottieLoadingIndicator,
    PortalFormDescription,
    PortalFormFieldGroup,
    PortalFormLabel,
    Toast,
    portalColors
} from "src/components";
import { TextLabel } from "../../style";
import { getAudiences } from "src/scenes/EmailCampaigns/api/audienceApi";
import ArrowDownIcon from "src/img/arrow-down-sign-to-navigate.svg";
import AudienceIcon from "src/img/emailCampaigns/Recipients@2x.png";
import Checkbox from "../../Checkbox";
import { NewCampaignContext } from "../NewCampaignContext";
import { customNames } from "src/utils/helpers";
import { USER_GROUP } from "src/constants/perks";
import { getEmailAudienceDisplayName } from "../../../constants";

const doesAudienceIncludeAnyContacts = (audience, contactUuids) => audience.contacts.some(contact => contactUuids.includes(contact.uuid));

const RecipientControls = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    min-width: 0;
`;

const RecipientSelectButton = styled.button`
    height: 46px;
    min-width: 12rem;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0 0.9rem;
    border: 1px solid ${p => p.$hasError ? portalColors.danger : portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-shadow: none;
    cursor: pointer;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
`;

const RecipientCountBadge = styled.div`
    height: 46px;
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0 0.9rem;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.blueSoft};
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
`;

const AudienceDropdown = styled.div`
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    z-index: 20;
    width: min(100vw - 3rem, 30rem);
    max-height: 30rem;
    overflow: auto;
    padding: 1rem;
    border: 1px solid ${portalColors.border};
    border-radius: 12px;
    background: ${portalColors.surface};
    box-shadow: ${portalColors.shadow};
    box-sizing: border-box;
`;

function AudienceSelectionRow(props) {
    const { index, audience, recipientContactUuids, onSelect, openEditSelection } = props;
    const anyContactsSelected = doesAudienceIncludeAnyContacts(audience, recipientContactUuids);
    const isDefaultAudience = [USER_GROUP.EMPLOYEES, USER_GROUP.CLIENTS].includes(audience.name);
    return (
        <FlexContainer
            key={index}
            direction="row"
            tDirection="row"
            mDirection="row"
            width="100%"
            justifyContent="space-between"
        >
            <FlexContainer flexGrow={1}>
                <TextLabel fontWeight="semibold" style={isDefaultAudience ? { textTransform: "capitalize" } : {}}>
                    {getEmailAudienceDisplayName(audience.name)}
                </TextLabel>
            </FlexContainer>
            <FlexContainer width="4rem" alignItems="center">
                <TextLabel fontWeight="semibold">
                    {audience.contactsCount}
                </TextLabel>
            </FlexContainer>
            <FlexContainer width="4rem" alignItems="center">
                <Checkbox
                    checked={anyContactsSelected}
                    onClickCallback={onSelect}
                />
            </FlexContainer>
            <FlexContainer width="8rem">
                <TextLabel
                    fontWeight="400"
                    color="#171A22"
                    style={{ display: anyContactsSelected ? "block" : "none", cursor: "pointer" }}
                    onClick={() => openEditSelection(audience)}
                >
                    Edit Selection
                </TextLabel>
            </FlexContainer>
        </FlexContainer>
    );
}

export default function Recipients() {
    const { errors, setErrors, recipientContactUuids, setRecipientContactUuids, openEditSelection } = useContext(NewCampaignContext);
    const [customAudienceList, setCustomAudienceList] = useState([]);
    const [showAudienceList, setShowAudienceList] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const ref = useRef(null);

    const audienceWithSelectionsCount = useMemo(() => {
        const audiences = customAudienceList.filter(audience => {
            return audience.contacts.some(contact => recipientContactUuids.includes(contact.uuid));
        });
        return audiences.length;
    }, [customAudienceList, recipientContactUuids]);

    const toggleShowAudienceList = () => setShowAudienceList(!showAudienceList);

    const getAllCustomAudience = async () => {
        try {
            setIsLoading(true);
            const response = await getAudiences();
            if (response) {
                setCustomAudienceList(response.customAudiences);
            }
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error occurred while fetching audiences.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getAllCustomAudience();
    }, []);

    const onSelectAudience = (audience) => {
        setErrors({ ...errors, recipients: "" });
        setRecipientContactUuids((prev) => {
            const isAlreadySelected = doesAudienceIncludeAnyContacts(audience, prev);
            if (isAlreadySelected) {
                return prev.filter(contactUuid => !audience.contacts.some(contact => contact.uuid === contactUuid));
            } else {
                return [...prev, ...audience.contacts.map(contact => contact.uuid)];
            }
        });
    };

    useEffect(() => {
        if (!showAudienceList) return;

        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setShowAudienceList(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showAudienceList]);

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Recipients
            </PortalFormLabel>
            <PortalFormDescription>
                Select who should receive this email. Choose from your Me Business users (e.g. <br/>
                {customNames.employees.toLowerCase()}, {customNames.clients.toLowerCase()}), custom audience groups, or select individuals.
            </PortalFormDescription>
            <div ref={ref}>
                <div style={{ position: "relative" }}>
                    <RecipientControls>
                        {isLoading ?
                            <FlexContainer
                                height="46px"
                                border="1px solid #D8DEEC"
                                borderRadius="8px"
                                padding="10px 15px 10px 15px"
                                justifyContent="space-between"
                                alignItems="center"
                                direction="row"
                                tDirection="row"
                                mDirection="row"
                                gap="10px"
                                width="180px"
                            >
                                <LottieLoadingIndicator
                                    height="2rem"
                                    width="2rem"
                                    margin="5px"
                                    containerHeight="2rem"
                                />
                            </FlexContainer> :
                            <RecipientSelectButton
                                type="button"
                                $hasError={!!errors.recipients}
                                onClick={() => toggleShowAudienceList()}
                            >
                                {audienceWithSelectionsCount > 0 ? `${audienceWithSelectionsCount} Selected` : "Select Recipients"}
                                <img src={ArrowDownIcon} width="15" height="15" />
                            </RecipientSelectButton>
                        }
                        <RecipientCountBadge>
                            <img src={AudienceIcon} width="19px" height="19px" />
                            {`${recipientContactUuids.length} Recipients Selected`}
                        </RecipientCountBadge>
                    </RecipientControls>
                    <ErrorText>{errors.recipients}</ErrorText>
                    {showAudienceList && (
                        <AudienceDropdown>
                            <FlexContainer>
                                <TextLabel fontSize="18px" fontWeight="bold">
                                        Select Recipients:
                                </TextLabel>
                                <TextLabel>
                                        Choose who will receive this email. The selected option will
                                        apply to this campaign only.
                                </TextLabel>
                            </FlexContainer>

                            <FlexContainer
                                width="100%"
                                direction="row"
                                tDirection="row"
                                mDirection="row"
                                justifyContent="space-between"
                                style={{ marginTop: "10px" }}
                            >
                                <FlexContainer flexGrow={1}>
                                    <TextLabel fontWeight="bold">Me Business Users:</TextLabel>
                                </FlexContainer>
                                <FlexContainer width="4rem" alignItems="center">
                                    <TextLabel fontWeight="bold">Size:</TextLabel>
                                </FlexContainer>
                                <FlexContainer width="4rem" alignItems="center">
                                    <TextLabel fontWeight="bold">Select:</TextLabel>
                                </FlexContainer>
                                <FlexContainer width="8rem">
                                    <TextLabel fontWeight="bold"></TextLabel>
                                </FlexContainer>
                            </FlexContainer>

                            {customAudienceList &&
                                    customAudienceList?.map((audience, index) => {
                                        if (audience.name !== "employees" && audience.name !== "clients") {
                                            return null;
                                        }
                                        return (
                                            <AudienceSelectionRow
                                                index={index}
                                                audience={audience}
                                                recipientContactUuids={recipientContactUuids}
                                                onSelect={() => onSelectAudience(audience)}
                                                openEditSelection={openEditSelection}
                                            />
                                        );
                                    })}
                            <FlexContainer
                                direction="row"
                                tDirection="row"
                                mDirection="row"
                                justifyContent="space-between"
                                style={{ marginTop: "15px" }}
                            >
                                <FlexContainer>
                                    <TextLabel fontWeight="bold">Custom Audiences:</TextLabel>
                                </FlexContainer>
                            </FlexContainer>
                            <FlexContainer style={{ overflow: "auto" }}>
                                {customAudienceList &&
                                        customAudienceList.map((audience, index) => {
                                            if (audience.name === "employees" || audience.name === "clients") {
                                                return null;
                                            }
                                            return (
                                                <AudienceSelectionRow
                                                    index={index}
                                                    audience={audience}
                                                    recipientContactUuids={recipientContactUuids}
                                                    onSelect={() => onSelectAudience(audience)}
                                                    openEditSelection={openEditSelection}
                                                />
                                            );
                                        })}
                            </FlexContainer>
                        </AudienceDropdown>
                    )}
                </div>
            </div>
        </PortalFormFieldGroup>
    );
}
